import { CheckBox, Spacer, VDBText } from "@components/atoms";
import React, { useEffect, useState } from "react";
import styled from "styled-components/native";
import { Image } from "expo-image";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon from "@components/icons/VDBIcon";
import { IconsVariant } from "@components/icons/VDBIcon";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import { VDBIcons } from "@utils/VDBIcons";
import { useTranslation } from "react-i18next";
import { getImageUrl, isImageUrlEmptyOrUndefined } from "../Utils";
import { ImageComponentWithSummaryTypes } from "../SearchResultListItem/types";
import { getProductIconColor } from "@scenes/favorites/utils/FavoritesUtils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "../PriceModeCard/types";
import { useOrderCart } from "@redux/slices/globalSlice";
import { getShowEditIcon } from "@scenes/bulk-request/Utils";
import { css } from "styled-components/native";
import { useProductInfo } from "@scenes/pdp/utils/utils";
import { useSelector } from "react-redux";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import { isPair } from "@utils/product/productValidators";
import { DefaultTheme } from "styled-components/native";
import { ProductCategory } from "@utils/constants";

const ImageComponent = ({
  mItem,
  priceMode,
  isCheckboxVisible = false,
  checkBoxSelection,
  ismItemChecked = false,
  backGroundColor,
  borderRadius,
  height,
  isBuilderItem,
  fromOrderRequest = false,
}: ImageComponentWithSummaryTypes) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const {
    enableOrderCart,
    enableShoppingCart,
    getPriceMode,
    disableImageZoom,
  } = useUserInfo();
  const [imageUri, setImageUri] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
      ? getImageUrl(mItem)
      : FALLBACK_IMAGE_ASSET
  );
  const [showImageContainer, setShowImageContainer] = useState(
    !isImageUrlEmptyOrUndefined(mItem)
  );
  const [showErrorImage, setShowErrorImage] = useState(
    isImageUrlEmptyOrUndefined(mItem)
  );
  const jewelryItemConfig =
    getPriceMode() === PriceModes.ORANGE && enableShoppingCart();

  const orderCart = useOrderCart()?.show_stock_no;
  const showStockNo = getShowEditIcon(orderCart, priceMode);

  const showStockNumber =
    isBuilderItem ||
    ((jewelryItemConfig || enableOrderCart()) && showStockNo) ||
    showStockNo;

  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);

  const { productName } = useProductInfo(
    t,
    mItem?.type || mItem?.stock_type,
    mItem,
    isCustomApp,
    fromOrderRequest
  );
  const [isItemChecked, setIsItemChecked] = useState(false);

  const checkStateChanged = (checked: boolean) => {
    setIsItemChecked(checked);
    checkBoxSelection(checked, mItem);
  };

  useEffect(() => {
    setIsItemChecked(ismItemChecked);
  }, [ismItemChecked]);

  return (
    <ImageAndContentContainer>
      <ImageAndCheckboxContainer
        backGroundColor={backGroundColor}
        applyRadius={borderRadius}
        height={height}
      >
        <>
          {isCheckboxVisible ? (
            <CheckBox
              fillColor={theme["knob-bor"]}
              checkBoxSize={20}
              checkedFunction={checkStateChanged}
              isChecked={isItemChecked}
              disabled={false}
            />
          ) : null}
          {showErrorImage ? (
            <FallbackImageContainer theme={theme}>
              <EmptyImagePreview
                shape={getShapeValue(mItem)}
                size={30}
                isPair={isPair(mItem) ?? false}
              />
            </FallbackImageContainer>
          ) : showImageContainer ? (
            <Image
              style={{
                height: 48,
                width: 48,
                borderRadius: 8,
                borderColor: theme["core-03"],
                borderWidth: 1,
              }}
              source={{
                uri: imageUri || FALLBACK_IMAGE_ASSET,
              }}
              onError={() => {
                if (mItem?.video_url) {
                  setShowImageContainer(false);
                } else {
                  setShowErrorImage(true);
                  setShowImageContainer(false);
                  setImageUri(FALLBACK_IMAGE_ASSET);
                }
              }}
              contentFit={disableImageZoom() ? "contain" : "cover"}
            />
          ) : (
            <VideoBackgroundContainer theme={theme}>
              <VideoPlayIconContainer theme={theme}>
                <VDBIcon
                  color={theme["core-06"]}
                  size={20}
                  variant={IconsVariant.VDBCustom}
                  name={VDBIcons.PLAY}
                />
              </VideoPlayIconContainer>
            </VideoBackgroundContainer>
          )}
        </>
      </ImageAndCheckboxContainer>
      <FirstContentContainer>
        {mItem?.short_title && (
          <>
            <VDBText
              type={typography["Minor-M"]}
              color={theme["core-05"]}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {mItem?.short_title}
            </VDBText>
          </>
        )}
        <Spacer size={SpaceType.ExtraSmall} />
        <FirstContentMapContainer>
          <VDBText
            color={theme[getProductIconColor(mItem?.type || mItem?.stock_type)]}
            type={typography["Minor-M"]}
          >
            {productName}
          </VDBText>
        </FirstContentMapContainer>
        <Spacer size={SpaceType.ExtraSmall} />
        {showStockNumber && mItem?.stock_num && (
          <TitleContainer>
            <TextContainer>
              <VDBText
                type={typography["Minor-R"]}
                color={theme["core-06"]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {mItem?.type === ProductCategory.JEWELRY
                  ? t("productDetail.jewelry.stockNo", "Stock#")
                  : t("searchResult.listView.stockNum.text")}
                :
              </VDBText>
              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText
                type={typography["Minor-M"]}
                color={theme["core-05"]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {theme.isMobile &&
                mItem?.stock_num != null &&
                mItem.stock_num.length > 20
                  ? `${mItem.stock_num.slice(0, 20)}…`
                  : mItem?.stock_num}
              </VDBText>
            </TextContainer>
          </TitleContainer>
        )}
      </FirstContentContainer>
    </ImageAndContentContainer>
  );
};

const FirstContentContainer = styled.View`
  justify-content: center;
  flex: 1;
  padding-top: ${SpaceType.ExtraSmall}px;
  padding-bottom: ${SpaceType.ExtraSmall}px;
`;

const FirstContentMapContainer = styled.View`
  flex-direction: row;
`;

const ImageAndCheckboxContainer = styled.View<{
  isCheckboxVisible?: boolean;
  backGroundColor?: string;
  applyRadius?: boolean;
  height?: string;
}>`
  height: ${({ height }: { height: number }) => (height ? height : 76)}px;
  padding-left: 8px;
  padding-right: 8px;
  background-color: ${({
    theme,
    backGroundColor,
  }: {
    theme: DefaultTheme;
    backGroundColor: keyof DefaultTheme;
  }) => (backGroundColor ? theme[backGroundColor] : theme["core-01"])};
  border-top-left-radius: 8px;
  ${({ applyRadius }: { applyRadius: boolean }) =>
    applyRadius &&
    css`
      border-bottom-left-radius: 8px;
    `}
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;

const ImageAndContentContainer = styled.View<any>`
  flex-direction: row;
  flex: 1;
`;

const TitleContainer = styled.View<any>``;

const VideoPlayIconContainer = styled.View<any>`
  height: 28px;
  width: 28px;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["over-02"]};
  border-radius: 14px;
`;

const VideoBackgroundContainer = styled.View<any>`
  height: 48px;
  width: 48px;
  border-radius: 8px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
`;

const FallbackImageContainer = styled.View<any>`
  width: 48px;
  height: 48px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  justify-content: center;
  align-items: center;
  border-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  border-width: 1px;
`;

const TextContainer = styled.View`
  flex-direction: row;
`;

export default React.memo(ImageComponent);
